import { useState, useEffect, useCallback } from 'react'
import FormControl from '@mui/material/FormControl'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Box from '@mui/material/Box'
import CheckboxAccordion from 'src/components/common/checkbox-accordion/CheckboxAccordion'
import { CheckboxItem } from 'src/components/common/checkbox-accordion/CheckboxList'
import { ExportConfigStep1, Column } from 'src/components/common/export-component/types'
import { StyledFormControlLabel } from 'src/components/common/export-component/steps/common'
import { SkeletonAccordion } from './Skeleton'
import { useRouter } from 'next/router'

interface Step1ContentProps {
  isLoading: boolean
  columns: Column[]
  config: ExportConfigStep1
  onChange: (config: ExportConfigStep1) => void
  onValidationChange: (isValid: boolean) => void
}

const Step1Content = ({ columns, isLoading, config, onChange, onValidationChange }: Step1ContentProps) => {
  const router = useRouter()
  const routePath =
    router.pathname === '/orders'
      ? 'orders'
      : router.pathname === '/abandoned-checkouts'
      ? 'abandoned_checkouts'
      : 'orders'

  const [error, setError] = useState<string>('')

  // Function to check if a column or any of its subcolumns are selected
  const isColumnOrSubcolumnsSelected = (column: Column): boolean => {
    if (config.selectedColumns.includes(column.name)) return true
    if (column.subcolumns) {
      return column.subcolumns.some(subcolumn => isColumnOrSubcolumnsSelected(subcolumn))
    }
    return false
  }

  // Function to validate selection
  const validateSelection = useCallback(() => {
    const hasSelection = columns.some(column => isColumnOrSubcolumnsSelected(column))
    if (!hasSelection) {
      setError('Please select at least one column to export')
      onValidationChange(false)
    } else {
      setError('')
      onValidationChange(true)
    }
  }, [columns, config.selectedColumns, onValidationChange])

  // Update validation whenever selection changes
  useEffect(() => {
    if (config.exportType !== 'today') {
      validateSelection()
    } else {
      onValidationChange(true)
    }
  }, [config.selectedColumns, validateSelection])

  const handleExportTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...config,
      exportType: event.target.value as ExportConfigStep1['exportType'],
      selectedColumns: []
    })
  }

  // Transform columns to match CheckboxAccordion item structure
  const transformColumns = (columns: Column[]): CheckboxItem[] => {
    return columns.map(column => ({
      id: column.name,
      name: column.title,
      checked: config.selectedColumns.includes(column.name),
      children: column.subcolumns ? transformColumns(column.subcolumns) : undefined,
      count: column.subcolumns ? column.subcolumns.length : undefined,
      description: column.description ? column.description : undefined
    }))
  }

  const getAllSelectedIds = (checkBoxData: readonly CheckboxItem[]): string[] => {
    const selectedIds: string[] = []

    checkBoxData.forEach(item => {
      if (item.checked) {
        selectedIds.push(item.id.toString())
      }
      if (item.children) {
        selectedIds.push(...getAllSelectedIds(item.children))
      }
    })

    return selectedIds
  }

  return (
    <>
      <h2 className='text-base font-semibold mt-2'>Export options</h2>
      <FormControl component='fieldset' sx={{ marginTop: '12px' }} disabled={isLoading}>
        <RadioGroup value={config.exportType} onChange={handleExportTypeChange} className='flex flex-col'>
          {routePath === 'orders' && (
            <>
              <StyledFormControlLabel value='orders' control={<Radio />} label='Export orders' />
              <StyledFormControlLabel value='refunds' control={<Radio />} label='Export refunds' />
              <StyledFormControlLabel value='today' control={<Radio />} label="Export today's orders" />
            </>
          )}
          {routePath === 'abandoned_checkouts' && (
            <>
              <StyledFormControlLabel value='abandoned_checkouts' control={<Radio />} label='Abandoned checkouts' />
              <StyledFormControlLabel value='all_checkouts' control={<Radio />} label='All checkouts' />
              <StyledFormControlLabel value='today' control={<Radio />} label='All checkouts for Today' />
            </>
          )}
        </RadioGroup>
      </FormControl>

      {config.exportType !== 'today' && (
        <>
          <h2 className='text-base font-semibold mt-6'>Select columns to export</h2>
          {isLoading ? (
            <SkeletonAccordion />
          ) : (
            <Box sx={{ maxHeight: '600px', overflow: 'auto', marginTop: '8px' }}>
              <CheckboxAccordion
                defaultCheckboxData={transformColumns(columns)}
                onChange={checkBoxData => {
                  const selectedColumns = getAllSelectedIds(checkBoxData)
                  onChange({
                    ...config,
                    selectedColumns: selectedColumns
                  })
                }}
              />
            </Box>
          )}
        </>
      )}
    </>
  )
}

export default Step1Content
